"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "food-storage-calculator",
  "refrigerator-lifespan-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function FreezerBTUCalculatorPageClient() {
  const meta = TOOL_METADATA["freezer-btu-calculator"]

  const [cubicFeet, setCubicFeet] = useState("15")
  const [location, setLocation] = useState("indoor")
  const [usage, setUsage] = useState("normal")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const size = readQuantity(cubicFeet, { min: 1, max: 50 })
    if (size === null) return null

    let baseBTU = size * 25 // ASHRAE-style 25 BTU/cu ft starting load
    if (location === "garage") baseBTU *= 1.3
    else if (location === "basement") baseBTU *= 0.9
    else if (location === "outdoor") baseBTU *= 1.5

    if (usage === "heavy") baseBTU *= 1.2
    else if (usage === "light") baseBTU *= 0.8

    const btuRequired = Math.round(baseBTU)
    let recommendation: string
    if (btuRequired < 500) recommendation = "Small chest freezer or compact upright"
    else if (btuRequired < 1000) recommendation = "Medium chest freezer or standard upright"
    else if (btuRequired < 1500) recommendation = "Large chest freezer or commercial-grade upright"
    else recommendation = "Extra-large or commercial freezer required"

    return { btuRequired, recommendation }
  }, [cubicFeet, location, usage])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="What BTU rating does"
      heroTitleItalic="your"
      heroTitlePost="freezer space need?"
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~20 sec", "ASHRAE heat-load principles"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "25", label: "BTU per cu ft", note: "Indoor base" },
            { value: "+30%", label: "Garage adjust", note: "Heat load" },
            { value: "+50%", label: "Outdoor adjust", note: "Ambient" },
            { value: "20%", label: "Safety margin", note: "Recommended" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "freezer-btu-calculator")}
      cta={{
        title: "Right-size your freezer. Real BTU spec sheets.",
        body: "Browse upright and chest freezers with verified compressor BTU ratings, energy use, and warranty info.",
        buttonLabel: "Browse all freezers",
        buttonHref: "/best-freezers",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Volume.
                <br />
                Location. Usage.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Garage and outdoor placement increases the heat load by 30–50%.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Freezer size
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={50}
                      step="0.1"
                      value={cubicFeet}
                      onChange={(e) => setCubicFeet(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">cu ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Installation location
                  </label>
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="indoor">Indoor (kitchen / pantry)</option>
                    <option value="basement">Basement (cooler ambient)</option>
                    <option value="garage">Garage (+30% load)</option>
                    <option value="outdoor">Outdoor / covered (+50% load)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Usage pattern
                  </label>
                  <select
                    value={usage}
                    onChange={(e) => setUsage(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="light">Light — occasional access (-20%)</option>
                    <option value="normal">Normal — daily access</option>
                    <option value="heavy">Heavy — frequent access (+20%)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Each door opening exchanges 10–20% of internal air.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Required cooling capacity
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.btuRequired ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">BTU/hr</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.recommendation ?? "Enter freezer dimensions and location."}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the BTU"
          title="Upright freezers in your range"
          description="Easier organization than chest models — and a smaller footprint for kitchens and pantries."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                ASHRAE heat-load math, <em className="italic text-[#ff9951]">simplified.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                A full ASHRAE calculation includes wall conduction, infiltration, product load, and internal sources. We approximate with volume × ambient × usage multipliers tuned to manufacturer BTU ratings.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Volume baseline",
                  d: "Cubic feet × 25 BTU/hr is the indoor starting load for a normally-loaded freezer at 0°F internal target.",
                },
                {
                  n: "02",
                  t: "Ambient adjustment",
                  d: "Garage installation adds 30%, outdoor placement adds 50%. Basements with cooler ambient subtract 10%.",
                },
                {
                  n: "03",
                  t: "Usage multiplier",
                  d: "Heavy door traffic adds 20% to the load. Light use (occasional access) subtracts 20%.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Chest freezers — most efficient style"
          description="Cold air sinks and stays put — chest freezers use roughly 20% less energy than uprights."
          collectionType="chest-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
